import { motion } from "framer-motion";
import { ClipboardList, Radar, ShieldCheck, Truck } from "lucide-react";

const STEPS = [
  {
    icon: ClipboardList,
    title: "Post requirements",
    desc: "Buyers list what they need — material, grade, quantity, location. Sellers list live inventory and availability.",
  },
  {
    icon: Radar,
    title: "Match in real time",
    desc: "Members instantly see live rates, verified supplier listings, and material availability matched to their exact requirement.",
  },
  {
    icon: ShieldCheck,
    title: "Trade with confidence",
    desc: "Deals are backed by verified vendors and test certificates for quality assurance — no more blind sourcing.",
  },
  {
    icon: Truck,
    title: "Track to delivery",
    desc: "Orders, logistics, and delivery status are tracked on-platform, removing the coordination chaos of manual follow-ups.",
  },
];

const JOURNEY = [
  "Enquiry (RFQ)", "Negotiation", "Order Confirmation", "Buyer PO", "Payment",
  "Order Preparation", "Logistics", "Delivered", "Proof of Delivery",
];

export function HowItWorks() {
  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-24 px-6 relative border-y border-ib-steel/10">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-14 max-w-3xl"
        >
          <span className="font-jetbrains text-xs text-ib-lime tracking-widest uppercase">How it works</span>
          <h2 className="font-outfit font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-3 text-ib-text">
            One platform, from <span className="text-ib-blue">discovery to delivery</span>.
          </h2>
          <p className="font-manrope text-base md:text-lg text-ib-text-secondary mt-4">
            Iron Byte digitizes the entire metal trading journey — turning a process built on calls and paperwork
            into a seamless, transparent digital flow. Source, negotiate, and close from anywhere, on any device.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bento-card rounded-lg border border-ib-steel/20 bg-ib-surface/50 p-6 relative"
                data-testid={`how-step-${i}`}
              >
                <span className="absolute top-4 right-4 font-jetbrains text-[10px] text-ib-text-muted">0{i + 1}</span>
                <div className="inline-flex p-2.5 rounded-lg bg-ib-blue/10 mb-4">
                  <Icon className="w-5 h-5 text-ib-blue" />
                </div>
                <h3 className="font-outfit font-bold text-base text-ib-text mb-2">{step.title}</h3>
                <p className="font-manrope text-sm text-ib-text-secondary leading-relaxed">{step.desc}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-10 overflow-x-auto"
          data-testid="trade-journey"
        >
          <div className="flex items-center gap-2 min-w-max px-1">
            {JOURNEY.map((stage, i) => (
              <div key={stage} className="flex items-center gap-2">
                <span className="font-jetbrains text-[11px] px-3 py-1.5 rounded-full border border-ib-steel/30 bg-ib-surface/50 text-ib-text-secondary whitespace-nowrap">
                  {stage}
                </span>
                {i < JOURNEY.length - 1 && <span className="w-4 h-px bg-ib-blue/50" />}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
